import {
  computed,
  ComputedRef,
  onMounted,
  ref,
  watch
} from '@vue/composition-api'
import {
  getWordId,
  getWordIndex,
  getWordTimecodes,
  isPunctuationElement,
  isPunctuationMark,
  isWordElement as isWordEl,
  punctuanions,
  Timecode
} from '../utils'
import Vue from 'vue'
import { UITranscript } from '../types/transcript'
import useTranscriptEditor from './useTranscriptEditor'
import { Word } from '../models/word'

const allowedSymbols = [
  ...punctuanions,
  '-',
  '@',
  '#',
  '$',
  '%',
  '^',
  '&',
  '*',
  '_',
  '+',
  '/',
  '|',
  '/'
]

interface Cursor {
  time: Timecode
  offset: number
}

const historyEnabled = false

export default function(_transcript?: UITranscript | null, isSafari = false) {
  const wordRemoved = ref<boolean>(false)
  const removedPart = ref<string>('')

  const cursorHistory = ref<Cursor[]>([])
  const cursorOffset = ref<number>(0)
  const cursor = ref<Selection | null>(null)
  const focusedWord = ref<HTMLElement | null>(null)
  const selectedPart = ref<Word | null>(_transcript?.parts[0] || null)
  const keydownEvent = ref<KeyboardEvent>()
  const conversationRef = ref<HTMLElement>()
  const editedParts = ref<Word[]>([])
  const touched = ref<boolean>(false)
  const saveTranscript = ref<boolean>(false)
  const undoHistory = ref<{ id: string; words: Word[] }[]>([])

  const {
    value: transcript,
    setValue: updateTranscript,
    version,
    ...transcriptEditor
  } = useTranscriptEditor(_transcript, isSafari)

  function updTranscript(transcript: UITranscript) {
    transcriptEditor.updTranscript(transcript)
  }

  /**
   * Adds the copy of the current cursor version to the history array
   */
  function updateCursorHistory() {
    if (!focusedWord.value) {
      return
    }

    cursorHistory.value.push({
      time: getWordTimecodes(focusedWord.value),
      offset: cursorOffset.value
    })
  }

  const tr = computed(() => transcript.value)

  /**
   * @returns the index of the focused part
   */
  const currentPartIndex: ComputedRef<number> = computed(() => {
    if (!transcript.value) {
      return -1
    }

    return transcript.value.parts.findIndex(p => {
      if (!selectedPart.value) {
        return false
      }
      return selectedPart.value.id === p.id
    })
  })

  /**
   * Gets an array of DOM words of a part by its index
   * @param index - index of a part
   */
  function getPartWordsByIndex(index: number) {
    if (!conversationRef.value || currentPartIndex.value === -1) {
      return []
    }

    const part = conversationRef.value.children[index]
    if (!part) {
      return []
    }
    const sentence = part.querySelector('.sentence')
    if (!sentence) {
      return []
    }

    return (
      (Array.from(sentence.children).filter(e =>
        e.className.includes('word')
      ) as HTMLElement[]) || []
    )
  }

  /**
   * Formats current part and ads it to history
   */
  function savePart() {
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)
    if (!selectedPart.value || !currentPartWords) {
      return
    }
    const formattedPart = transcriptEditor.formatPart(
      selectedPart.value,
      currentPartWords
    )

    if (!formattedPart) {
      return
    }

    // TODO remove here history update

    if (!historyEnabled) {
      return
    }

    if (!undoHistory.value.length) {
      undoHistory.value.push({
        id: selectedPart.value.id as string,
        words: selectedPart.value.words as Word[]
      })
    }
    undoHistory.value.push({
      id: selectedPart.value.id as string,
      words: formattedPart
    })
  }

  const save = () => {
    savePart()
    saveTranscript.value = true
  }

  /**
   * Adds part to the array of edited parts
   * @param part - part to add to the array of edited parts
   */
  function addEditedPart(part: Word) {
    if (!editedParts.value.find(p => p.id === part.id)) {
      editedParts.value.push(part)
    }
  }

  const currentPart: ComputedRef<HTMLElement | null> = computed(() => {
    if (!conversationRef.value || currentPartIndex.value === -1) {
      return null
    }
    const part = conversationRef.value.children[currentPartIndex.value]
    if (!part) {
      return null
    }
    return part.querySelector('.sentence')
  })

  /**
   * Adds focused part to the array of edited parts
   */
  function setCurrentPartEdited() {
    if (!selectedPart.value) {
      return
    }
    selectedPart.value.edited = true
    addEditedPart(selectedPart.value)
  }

  /**
   * Checks whether an element is space element or not
   * @param el - html element to check
   */
  function isSpaceElement(el: HTMLElement) {
    return el.className.includes('space')
  }

  /**
   * Gets an array of DOM words of previous part before focused one
   */
  const previousPartWords: ComputedRef<HTMLElement[]> = computed(() => {
    return getPartWordsByIndex(currentPartIndex.value - 1)
  })

  /**
   * Checks whether a node is space node or not
   * @param node - html node to check
   */
  function isSpaceNode(node: Node | null): boolean {
    if (!node) {
      return false
    }

    return !!(node.parentElement && isSpaceElement(node.parentElement))
  }

  /**
   * Finds closest next word in DOM
   * @param focusElement - element to find closest word to
   */
  function findClosestPrevWord(focusElement: HTMLElement): HTMLElement | null {
    let prevElement = focusElement.previousElementSibling

    if (!prevElement) {
      return null
    }

    let isPrevElementWord = isWordEl(prevElement as HTMLElement)

    while (!isPrevElementWord && prevElement?.previousElementSibling) {
      prevElement = prevElement.previousElementSibling
      isPrevElementWord = isWordEl(prevElement as HTMLElement)
    }

    if (!isPrevElementWord) {
      return null
    }

    return prevElement as HTMLElement
  }

  /**
   * Defines whether cursor is at the end of part
   */
  const isCursorAtTheEndOfPart: ComputedRef<boolean> = computed(() => {
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    if (!focusedWord.value || !currentPartWords || !cursor.value) {
      return false
    }

    return (
      getWordIndex(focusedWord.value) ===
        getWordIndex(currentPartWords[currentPartWords.length - 1]) &&
      cursor.value.focusOffset >= focusedWord.value.innerText.trim().length
    )
  })

  /**
   * Finds closest previous word in DOM
   * @param focusElement - element to find closest word to
   */
  function findClosestNextWord(focusElement: HTMLElement): HTMLElement | null {
    let nextElement = focusElement.nextElementSibling

    if (!nextElement) {
      return null
    }

    let isNextElementWord = isWordEl(nextElement as HTMLElement)

    while (!isNextElementWord && nextElement.nextElementSibling) {
      nextElement = nextElement.nextElementSibling
      isNextElementWord = isWordEl(nextElement as HTMLElement)
    }

    if (!isNextElementWord) {
      return null
    }

    return nextElement as HTMLElement
  }

  /**
   * Defines whether cursor is at the start of part
   */
  const isCursorAtTheStartOfPart: ComputedRef<boolean> = computed(() => {
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    if (!focusedWord.value || !currentPartWords || !cursor.value) {
      return false
    }

    return (
      getWordId(focusedWord.value) === getWordId(currentPartWords[0]) &&
      cursor.value.focusOffset === 0
    )
  })

  /**
   * Updates focused word according to the cursor focus node
   */
  function updateWordFocus(focusNode: Node | null) {
    if (!focusNode) {
      return
    }

    if (isWordEl(focusNode as HTMLElement)) {
      focusedWord.value = focusNode as HTMLElement
      updateCursorHistory()
    } else if (isWordEl(focusNode.parentElement as HTMLElement)) {
      focusedWord.value = focusNode.parentElement as HTMLElement
      updateCursorHistory()
      return
    } else if (isSpaceNode(focusNode)) {
      const next = findClosestNextWord(focusNode.parentElement as HTMLElement)

      let offset = 0
      if (next) {
        offset = 0
        focusedWord.value = next
      } else {
        const prev = findClosestPrevWord(focusNode.parentElement as HTMLElement)
        if (!prev) {
          return
        }
        offset = prev.innerText.length
        focusedWord.value = prev
      }

      cursorHistory.value.push({
        time: getWordTimecodes(focusedWord.value),
        offset
      })
    }

    return
  }

  /**
   * Adds the copy of the current transcript version to the history array
   */
  function updateHistory() {
    // const testTrue = true
    if (!transcript.value) {
      return
    }

    cursorHistory.value.unshift({
      time: getWordTimecodes(focusedWord.value),
      offset: cursorOffset.value
    })

    // if (historyEnabled) {
    //     return
    // }

    // history.value.unshift(JSON.parse(JSON.stringify(transcript.value)))
  }

  /**
   * Manually updates cursor
   * @param el - element to focus
   * @param offset - position in text to put cursor
   */
  function setCursor(el?: Node | HTMLElement, offset = 0) {
    if (!el) {
      return
    }

    const range = new Range()

    let node
    if (el instanceof HTMLElement) {
      node = el.childNodes[0]
    } else {
      node = el
    }

    if (!node) {
      return
    }

    if (!node.nodeValue || !node.nodeValue.trim().length) {
      node.nodeValue = String.fromCharCode(160)
    }

    range.setStart(node, offset)
    range.setEnd(node, offset)

    if (cursor.value) {
      cursor.value.removeAllRanges()
      cursor.value.addRange(range)

      if (!cursor.value.focusNode) {
        return
      }

      cursorOffset.value = offset
      updateWordFocus(cursor.value.focusNode)
    }
  }

  /**
   * Sets cursor on word according to its index in the current part
   * @param wordIndex - index of the word
   * @param offset - offset to set cursor too
   */
  function setCursorOnWord(wordIndex: number, offset = 0) {
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    setCursor(currentPartWords[wordIndex], offset)
  }

  /**
   * Don't remember, do not touch :)
   */
  function restoreCursor(afterUpdate = false) {
    if (!focusedWord.value) {
      return
    }

    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    let restoredNode =
      currentPartWords.find(
        w => getWordId(w) === getWordId(focusedWord.value as HTMLElement)
      ) || findClosestNextWord(focusedWord.value)

    let restoredPosition = 0
    if (afterUpdate) {
      restoredPosition =
        cursorHistory.value[cursorHistory.value.length - 1]?.offset || 0
    }

    if (!restoredNode) {
      restoredNode = currentPartWords[currentPartWords.length - 1]
      restoredPosition = restoredNode.innerText.trim().length
    }

    setCursor(restoredNode, restoredPosition)
  }

  /**
   * Do not remember, do not touch :)
   */
  function restoreCursorAfterSelectedPartChange(
    newPart: Word,
    shouldRestoreCursor = true
  ) {
    setTimeout(() => {
      selectedPart.value = newPart

      Vue.nextTick(() => {
        if (shouldRestoreCursor) {
          restoreCursor()
        }
      })
    })
  }

  /**
   * Formats edited parts
   */
  function saveEditedParts() {
    if (!editedParts.value.length) {
      return
    }

    const partsDomWords: HTMLElement[][] = []
    editedParts.value.forEach(part => {
      partsDomWords.push(getPartWordsByIndex(part.index))
    })

    transcriptEditor.formatEdited(editedParts.value, partsDomWords)
    editedParts.value = []
  }

  /**
   * Inserts empty part before the focused one
   */
  function insertEmptyPartBefore() {
    if (currentPartIndex.value === -1) {
      return
    }

    editedParts.value = editedParts.value.filter(
      ep => ep.index !== currentPartIndex.value
    )
    saveEditedParts()
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    const newPart = transcriptEditor.insertEmptyPartBefore(
      currentPartIndex.value,
      currentPartWords,
      selectedPart.value!.speaker
    )

    if (!newPart) {
      return
    }

    selectedPart.value = newPart

    Vue.nextTick(() => {
      const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

      if (currentPartWords) {
        setCursor(currentPartWords[0])
      }
    })
  }

  /**
   * Inserts empty part after the focused one
   */
  function insertEmptyPart() {
    if (currentPartIndex.value === -1) {
      return
    }
    editedParts.value = editedParts.value.filter(
      ep => ep.index !== currentPartIndex.value
    )
    saveEditedParts()

    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)
    const newPart = transcriptEditor.insertEmptyPart(
      currentPartIndex.value,
      currentPartWords,
      selectedPart.value!.speaker
    )

    if (!newPart) {
      return
    }

    selectedPart.value = newPart

    saveTranscript.value = true

    Vue.nextTick(() => {
      const currentPartWords = getPartWordsByIndex(currentPartIndex.value)
      if (currentPartWords) {
        setCursor(currentPartWords[0])
      }
    })
  }

  /**
   * Splits focused part according to the cursor position
   */
  function splitPart() {
    if (!focusedWord.value || !cursor.value) {
      return
    }

    editedParts.value = editedParts.value.filter(
      ep => ep.index !== currentPartIndex.value
    )
    saveEditedParts()
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    const newPart: Word | null | undefined = transcriptEditor.splitPart(
      currentPartIndex.value,
      currentPartWords,
      focusedWord.value,
      cursorHistory.value[cursorHistory.value.length - 1].offset,
      selectedPart.value!.speaker,
      focusedWord.value.className.includes('single'),
      findClosestNextWord(focusedWord.value)
    )

    if (!newPart) {
      return
    }

    selectedPart.value = null
    selectedPart.value = newPart
    Vue.nextTick(() => {
      const currentPartWords = getPartWordsByIndex(currentPartIndex.value)
      setCursor(currentPartWords[0], 0)
    })
  }

  /**
   * Resolves behavior after pressing the ENTER button
   */
  function handlePressEnter() {
    keydownEvent.value?.preventDefault()
    updateHistory()

    if (isCursorAtTheEndOfPart.value) {
      insertEmptyPart()
    } else if (isCursorAtTheStartOfPart.value) {
      insertEmptyPartBefore()
    } else {
      if (
        cursor.value &&
        focusedWord.value &&
        focusedWord.value.className.includes('single')
      ) {
        const idx = cursor.value.focusOffset
        const text = focusedWord.value.innerText
        let textBeforeCursor = ''
        let textAfterCursor = ''
        for (let i = idx; idx > 0; i--) {
          if (text[i - 1] && text[i - 1].trim().length) {
            textBeforeCursor = text[i - 1].concat(textBeforeCursor)
          } else {
            break
          }
        }

        for (let i = idx; idx < text.length; i++) {
          if (text[i] && text[i].trim().length) {
            textAfterCursor = textAfterCursor.concat(text[i])
          } else {
            break
          }
        }

        const focusedWordId = getWordId(focusedWord.value)
        let cursorPositionAtWord = textBeforeCursor.length

        let cursorWordText = ''

        if (isPunctuationMark(textBeforeCursor)) {
          cursorWordText = textAfterCursor
          cursorPositionAtWord = 0
        } else if (isPunctuationMark(textAfterCursor)) {
          cursorWordText = textBeforeCursor
        } else {
          cursorWordText = textBeforeCursor.concat(textAfterCursor)
        }

        const wordWords = transcriptEditor.extractWords(text)
        const wordWordsWithSameText = wordWords.filter(
          w => w === cursorWordText
        )

        let restoredString = ''
        wordWords.forEach((w, i) => {
          if (wordWords[i + 1] && isPunctuationMark(wordWords[i + 1])) {
            restoredString += wordWords[i]
          } else {
            restoredString += `${wordWords[i]} `
          }
        })

        const wordOffsetIndex = wordWords.indexOf(cursorWordText)
        const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

        if (wordOffsetIndex !== -1) {
          const newPart = transcriptEditor.saveWords(
            currentPartIndex.value,
            currentPartWords
          )

          if (!newPart) {
            return
          }

          selectedPart.value = null
          selectedPart.value = newPart

          setTimeout(() => {
            Vue.nextTick(() => {
              const currentPartWords = getPartWordsByIndex(
                currentPartIndex.value
              )

              const focusWordIndex = currentPartWords.findIndex(
                w => getWordId(w) === focusedWordId
              )
              setCursor(
                currentPartWords[focusWordIndex + wordOffsetIndex],
                cursorPositionAtWord
              )
              splitPart()
              return
            })
          })
        }
        return

        // const wordIdx =

        // if (restoredString.length < cursor.value.focusOffset) {
        //     //
        // }
        //
        // const initialStringBeforeCursor = text.slice(0, cursor.value.focusOffset)
        // const restoredStringBeforeCursor = restoredString.slice(0)
        //
        // const cursorPos =
        //     cursor.value.focusOffset -
        //     (initialStringBeforeCursor.length - restoredStringBeforeCursor.length)

        if (!wordWordsWithSameText.length) {
          return
        }

        if (wordWordsWithSameText.length > 1) {
          //
        }

        // const words = transcriptEditor.extractWords(focusedWord.value.innerText).join('')
      }
      splitPart()
    }
  }

  function removeFocusedWord(nextFocus: HTMLElement | null) {
    if (!currentPart.value || !focusedWord.value) {
      return
    }

    if (nextFocus) {
      focusedWord.value.textContent = String.fromCharCode(160)
      setCursor(focusedWord.value)
    } else {
      focusedWord.value.textContent = String.fromCharCode(160)
      setCursor(focusedWord.value)
    }
  }

  /**
   * Joins focused word with the next word
   */
  function joinFocusedWordWithNextOne() {
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    if (!focusedWord.value || !currentPartWords) {
      return
    }

    const nextWord = findClosestNextWord(focusedWord.value)
    if (!nextWord) {
      return
    }

    updateHistory()
    editedParts.value = editedParts.value.filter(
      ep => ep.index !== currentPartIndex.value
    )
    saveEditedParts()

    const id = getWordId(nextWord)
    const res = transcriptEditor.joinWords(
      currentPartIndex.value,
      id,
      currentPartWords
    )

    if (!res) {
      return
    }

    const [updatedPart, restoredWord, restoredOffset] = res

    if (!updatedPart || !updatedPart.words) {
      return
    }

    selectedPart.value = updatedPart
    // setCurrentPartEdited()
    Vue.nextTick(() => {
      if (restoredWord && restoredWord.partIndex !== undefined) {
        const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

        let idx = currentPartWords.findIndex(
          w => getWordId(w) === restoredWord.id
        )
        if (idx === -1) {
          idx = 0
        }
        setCursorOnWord(idx, restoredOffset)
      }
    })
  }

  /**
   * Removes a punctuation symbol
   * @param wordId - id of the punctuation word
   * @param join - defines whether we should join focused word with the next one after deleting
   */
  function removePunctuationWord(wordId: string, join = false) {
    if (!focusedWord.value || !selectedPart.value) {
      return
    }

    updateHistory()
    editedParts.value = editedParts.value.filter(
      ep => ep.index !== currentPartIndex.value
    )
    saveEditedParts()
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    if (historyEnabled) {
      undoHistory.value = []
      undoHistory.value.push({
        id: selectedPart.value.id as string,
        words: selectedPart.value.words as Word[]
      })
    }

    const res = transcriptEditor.removePunctuation(
      currentPartIndex.value,
      wordId,
      currentPartWords
    )

    if (!res) {
      return
    }

    const [newPart, restoreId] = res

    selectedPart.value = null
    selectedPart.value = newPart
    Vue.nextTick(() => {
      const currentPartWords = getPartWordsByIndex(currentPartIndex.value)
      savePart()
      let idx = currentPartWords.findIndex(w => getWordId(w) === restoreId)
      if (idx === -1) {
        idx = 0
      }

      setCursorOnWord(idx, currentPartWords[idx].innerText.length)

      if (join) {
        joinFocusedWordWithNextOne()
      }
    })
  }

  /**
   * Ensures that the first word's DOM node of the part is not deleted
   */
  function leaveWordEmptyIfTheFirstWord() {
    if (!focusedWord.value || !currentPart.value || !keydownEvent.value) {
      return
    }

    const prevWord = findClosestPrevWord(focusedWord.value)
    const nextWord = findClosestNextWord(focusedWord.value)

    // Leaves word empty is it is the first word in part
    if (!prevWord) {
      keydownEvent.value.preventDefault()
      updateHistory()
      removeFocusedWord(nextWord)
      return
    }

    updateHistory()
    keydownEvent.value.preventDefault()
    focusedWord.value.textContent = null

    if (isSafari) {
      setCursor(prevWord, prevWord.innerText.length)
    } else {
      setCursor(focusedWord.value, 0)
    }
  }

  /**
   * Deleting a letter by pressing BACKSPACE button
   */
  function handleTextBackspace() {
    if (!cursor.value || !keydownEvent.value || !focusedWord.value) {
      return
    }

    const node = focusedWord.value.cloneNode(true) as HTMLElement

    updateHistory()

    switch (node.innerText.length) {
      case 1: {
        if (isPunctuationElement(focusedWord.value)) {
          keydownEvent.value.preventDefault()
          removePunctuationWord(
            getWordId(focusedWord.value),
            !isSpaceElement(focusedWord.value.nextElementSibling as HTMLElement)
          )
          break
        }

        setCurrentPartEdited()
        leaveWordEmptyIfTheFirstWord()
        save()
        break
      }
      default: {
        setCurrentPartEdited()
        save()
        break
      }
    }
  }

  /**
   * Delete selected text range
   */
  function handleRangeDelete() {
    if (
      !cursor.value ||
      !cursor.value.anchorNode ||
      !cursor.value.focusNode ||
      !focusedWord.value ||
      !keydownEvent.value ||
      !selectedPart.value
    ) {
      return
    }

    updateHistory()

    if (focusedWord.value.className.includes('single')) {
      const prevWord = findClosestPrevWord(focusedWord.value)
      const nextWord = findClosestPrevWord(focusedWord.value)

      setCurrentPartEdited()

      // Remain at least 1 word at part
      if (
        Math.abs(cursor.value.focusOffset - cursor.value.anchorOffset) ===
          cursor.value.focusNode.textContent?.length &&
        !prevWord &&
        !nextWord
      ) {
        keydownEvent.value.preventDefault()
        focusedWord.value.textContent = String.fromCharCode(160)
        setCursor(focusedWord.value, 0)
        return
      }

      if (
        cursor.value.focusOffset - cursor.value.anchorOffset !==
        focusedWord.value.innerText.length
      ) {
        return
      }
    }

    keydownEvent.value.preventDefault()

    const focusedId = getWordId(
      isWordEl(cursor.value.anchorNode.parentElement as HTMLElement)
        ? (cursor.value.anchorNode.parentElement as HTMLElement)
        : (cursor.value.anchorNode as HTMLElement)
    )

    if (!focusedId) {
      return
    }

    editedParts.value = editedParts.value.filter(
      ep => ep.index !== currentPartIndex.value
    )
    saveEditedParts()

    if (historyEnabled) {
      undoHistory.value = []
      undoHistory.value.push({
        id: selectedPart.value.id as string,
        words: selectedPart.value.words as Word[]
      })
    }

    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    if (historyEnabled) {
      undoHistory.value = []
      undoHistory.value.push({
        id: selectedPart.value.id as string,
        words: selectedPart.value.words as Word[]
      })
    }

    const res = transcriptEditor.deleteWords(
      currentPartIndex.value,
      cursor.value.anchorNode as HTMLElement,
      cursor.value.focusNode as HTMLElement,
      currentPartWords
    )

    if (!res) {
      return
    }

    const [newPart, focusId] = res

    if (!newPart || !newPart.words) {
      return
    }

    selectedPart.value = null
    restoreCursorAfterSelectedPartChange(newPart, false)
    const w = newPart.words.find(w => w.id === focusId)

    if (!w) {
      return
    }

    setTimeout(() => {
      Vue.nextTick(() => {
        savePart()
        const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

        let idx = currentPartWords.findIndex(_w => getWordId(_w) === w.id)
        if (idx === -1) {
          idx = 0
        }

        let currentText = currentPartWords[idx].textContent
        if (!currentText) {
          currentText = ''
        }

        currentPartWords[idx].textContent = currentText.concat(
          String.fromCharCode(160)
        )
        setCursor(currentPartWords[idx], currentPartWords[idx].innerText.length)
      })
    })

    // setCurrentPartEdited()
  }

  /**
   * Joins focused word with the previous one
   */
  function joinFocusedWordWithPreviousOne() {
    if (!focusedWord.value || !keydownEvent.value || !selectedPart.value) {
      return
    }

    updateHistory()

    keydownEvent.value.preventDefault()
    editedParts.value = editedParts.value.filter(
      ep => ep.index !== currentPartIndex.value
    )
    saveEditedParts()

    const id = getWordId(focusedWord.value)
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    if (historyEnabled) {
      undoHistory.value = []
      undoHistory.value.push({
        id: selectedPart.value.id as string,
        words: selectedPart.value.words as Word[]
      })
    }

    const res = transcriptEditor.joinWords(
      currentPartIndex.value,
      id,
      currentPartWords
    )

    if (!res) {
      return
    }

    const [updatedPart, restoredWord, restoredOffset] = res

    if (!updatedPart || !updatedPart.words) {
      return
    }

    selectedPart.value = updatedPart

    setTimeout(() => {
      Vue.nextTick(() => {
        if (restoredWord && restoredWord.partIndex !== undefined) {
          const currentPartWords = getPartWordsByIndex(currentPartIndex.value)
          savePart()

          let idx = currentPartWords.findIndex(
            w => getWordId(w) === restoredWord.id
          )

          if (idx === -1) {
            idx = 0
          }
          setCursorOnWord(idx, restoredOffset)
        }
      })
    })
  }

  /**
   * Backspace button press if the cursor is at the beginning of a word DOM node
   */
  function resolveBackspaceWhenCursorIsAtTheBeginningOfWord() {
    if (!focusedWord.value || !keydownEvent.value) {
      return
    }

    updateHistory()

    const prevWord = findClosestPrevWord(focusedWord.value)

    if (prevWord) {
      if (!prevWord.innerText.trim().length || !prevWord.nextElementSibling) {
        setCurrentPartEdited()
        return
      }

      if (
        isPunctuationElement(prevWord) &&
        prevWord.innerText.length === 1 &&
        !isSpaceElement(prevWord.nextElementSibling as HTMLElement)
      ) {
        keydownEvent.value.preventDefault()
        removePunctuationWord(getWordId(prevWord))
      } else if (
        !isPunctuationElement(prevWord) &&
        !isPunctuationMark(prevWord.innerText[prevWord.innerText.length - 1]) &&
        !isPunctuationMark(focusedWord.value.innerText[0]) &&
        isSpaceElement(prevWord.nextElementSibling as HTMLElement)
      ) {
        joinFocusedWordWithPreviousOne()
      }

      return
    } else {
      updateHistory()
      keydownEvent.value.preventDefault()

      //

      editedParts.value = editedParts.value.filter(
        ep =>
          ep.index !== currentPartIndex.value - 1 &&
          ep.index !== currentPartIndex.value
      )
      saveEditedParts()

      const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

      const newPart = transcriptEditor.joinParts(
        currentPartIndex.value - 1,
        currentPartIndex.value,
        previousPartWords.value,
        currentPartWords
      )

      if (!newPart) {
        return
      }

      selectedPart.value = null
      restoreCursorAfterSelectedPartChange(newPart)
    }
  }

  /**
   * Checks whether a part is empty
   * @param part - part to check
   */
  function isPartEmpty(part: Word): boolean {
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    if (
      !part ||
      !part.words ||
      !part.words.length ||
      !currentPartWords ||
      !currentPartWords.length
    ) {
      return true
    }

    if (part.words.length === 1) {
      return !currentPartWords[0].innerText.trim().length
    } else {
      return (
        part.words.filter((w, i) => {
          return currentPartWords[i]
            ? !currentPartWords[i].innerText.trim().length
            : true
        }).length === part.words.length
      )
    }
  }

  /**
   * Resolves some behavior after pressing the BACKSPACE button
   */
  function handlePressBackspace() {
    if (
      !cursor.value ||
      !cursor.value.focusNode?.parentElement ||
      !keydownEvent.value ||
      !transcript.value ||
      !focusedWord.value ||
      currentPartIndex.value === -1
    ) {
      return
    }

    if (cursor.value.type === 'Range') {
      handleRangeDelete()
      return
    }

    const SAFARIisCursorAtTheBeginning = cursor.value.focusOffset === 0
    const OTHERSisCursorAtTheBeginning = isSpaceElement(
      cursor.value.focusNode.parentElement
    )
    const isCursorAtTheBeginning =
      SAFARIisCursorAtTheBeginning || OTHERSisCursorAtTheBeginning

    if (!isCursorAtTheBeginning) {
      handleTextBackspace()
      return
    }

    if (!focusedWord.value.innerText.trim().length) {
      if (selectedPart.value && isPartEmpty(selectedPart.value)) {
        keydownEvent.value.preventDefault()

        const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

        // delete part
        const updatedPart = transcriptEditor.deletePart(
          currentPartIndex.value,
          currentPartWords
        )
        if (!updatedPart) {
          return
        }

        selectedPart.value = updatedPart
        Vue.nextTick(() => {
          const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

          setCursor(
            currentPartWords[currentPartWords.length - 1],
            currentPartWords[currentPartWords.length - 1].innerText.length
          )
        })
      }
    } else {
      resolveBackspaceWhenCursorIsAtTheBeginningOfWord()
    }
  }

  /**
   * Resolves some behavior after pressing the DELETE button
   */
  function handlePressDelete() {
    if (
      !cursor.value ||
      !cursor.value.focusNode?.parentElement ||
      !keydownEvent.value ||
      !transcript.value ||
      !focusedWord.value ||
      currentPartIndex.value === -1
    ) {
      return
    }

    if (cursor.value.type === 'Range') {
      handleRangeDelete()
    }

    updateHistory()
    setCurrentPartEdited()

    const isCursorAtTheEndOfWord =
      cursor.value.focusOffset === focusedWord.value.innerText.length

    if (!isCursorAtTheEndOfWord) {
      return
    }

    const nextWord = findClosestNextWord(focusedWord.value)

    if (!nextWord || !focusedWord.value.innerText.trim().length) {
      // TODO: join parts mb?
      return
    }

    if (isPunctuationElement(nextWord)) {
      keydownEvent.value.preventDefault()
      removePunctuationWord(getWordId(nextWord))
      return
    }

    if (
      !isPunctuationElement(focusedWord.value) &&
      !!focusedWord.value.nextElementSibling &&
      !isPunctuationMark(nextWord.innerText[0])
    ) {
      keydownEvent.value.preventDefault()
      joinFocusedWordWithNextOne()
      return
    }
  }

  /**
   * Making sure that each word is focused properly and avoiding focus on space
   */
  function formatCursor() {
    if (!cursor.value || !cursor.value.focusNode) {
      return
    }

    if (!isSpaceNode(cursor.value.focusNode)) {
      return
    } else {
      focusedWord.value = findClosestNextWord(
        cursor.value.focusNode.parentElement as HTMLElement
      )

      if (focusedWord.value) {
        setCursor(focusedWord.value, 0)
        return
      }

      focusedWord.value = findClosestPrevWord(
        cursor.value.focusNode.parentElement as HTMLElement
      )

      if (focusedWord.value) {
        setCursor(focusedWord.value, focusedWord.value.innerText.length)
        return
      }
    }
  }

  /**
   * Handling cursor change
   */
  function updateCursor(): boolean {
    if (!window.getSelection) {
      return false
    }

    cursor.value = null
    cursor.value = window.getSelection()

    if (!cursor.value || !cursor.value.focusNode) {
      return false
    }

    cursorOffset.value = cursor.value.focusOffset
    updateCursorHistory()
    updateWordFocus(cursor.value.focusNode)

    return true
  }

  /**
   * Handling cursor change using mouse
   */
  function setMouseCursor(part: Word) {
    if (part.id === selectedPart.value?.id) {
      //
    } else {
      selectedPart.value = part
    }

    setTimeout(() => {
      // Vue.nextTick(() => {
      // updateCursor()
      if (isPartEmpty(part) && selectedPart.value) {
        const currentPartWords = getPartWordsByIndex(currentPartIndex.value)
        setCursor(currentPartWords[0], 0)
      } else {
        updateCursor()
      }
      // })
    })
  }

  /**
   * Inserting text at the beginning of a word DOM node
   * @param text - text to insert
   */
  function insertCharacterAtTheBeginningOfWord(text: string) {
    if (!keydownEvent.value || !focusedWord.value) {
      return
    }

    keydownEvent.value.preventDefault()

    let currentText = focusedWord.value.textContent
    if (!currentText) {
      currentText = ''
    }

    focusedWord.value.textContent = text.concat(currentText)
    setCursor(focusedWord.value, text.length)

    if (!focusedWord.value.className.includes('single')) {
      focusedWord.value.classList.add('single')
    }

    setCurrentPartEdited()
  }

  /**
   * Inserting text at the specific position of a word DOM node
   * @param text - text to insert
   * @param position - position to insert into
   */
  function insertCharacterAtSpecificPosition(text: string, position = 1) {
    if (!keydownEvent.value || !focusedWord.value) {
      return
    }

    keydownEvent.value.preventDefault()

    let currentText = focusedWord.value.textContent
    if (!currentText) {
      currentText = ''
    }

    focusedWord.value.textContent = currentText
      .slice(0, position)
      .concat(text)
      .concat(currentText.slice(position, currentText.length))
    setCursor(focusedWord.value, position + text.length)

    if (!focusedWord.value.className.includes('single')) {
      focusedWord.value.classList.add('single')
    }

    setCurrentPartEdited()
  }

  /**
   * Inserts text in a selected text range
   * @param text  - text to insert
   * @returns whether the operation is successful
   */
  function insertInRange(text: string): boolean {
    if (
      !cursor.value ||
      !cursor.value.anchorNode ||
      !focusedWord.value ||
      !keydownEvent.value ||
      !selectedPart.value
    ) {
      return false
    }

    updateHistory()

    if (focusedWord.value.className.includes('single')) {
      insertCharacterAtSpecificPosition(
        text,
        Math.min(cursor.value.focusOffset, cursor.value.anchorOffset)
      )
    }

    const focusedId = getWordId(
      isWordEl(cursor.value.anchorNode.parentElement as HTMLElement)
        ? (cursor.value.anchorNode.parentElement as HTMLElement)
        : (cursor.value.anchorNode as HTMLElement)
    )

    if (!focusedId) {
      return false
    }

    editedParts.value = editedParts.value.filter(
      ep => ep.index !== currentPartIndex.value
    )
    saveEditedParts()
    const currentPartWords = getPartWordsByIndex(currentPartIndex.value)

    if (historyEnabled) {
      undoHistory.value = []
      undoHistory.value.push({
        id: selectedPart.value.id as string,
        words: selectedPart.value.words as Word[]
      })
    }

    const res = transcriptEditor.deleteWords(
      currentPartIndex.value,
      cursor.value.anchorNode as HTMLElement,
      cursor.value.focusNode as HTMLElement,
      currentPartWords
    )

    if (!res) {
      return false
    }

    const [newPart, focusId] = res

    if (!newPart || !newPart.words) {
      return false
    }

    selectedPart.value = null
    restoreCursorAfterSelectedPartChange(newPart, false)

    const w = newPart.words.find(w => w.id === focusId)

    if (!w) {
      return false
    }

    setTimeout(() => {
      Vue.nextTick(() => {
        const currentPartWords = getPartWordsByIndex(currentPartIndex.value)
        savePart()

        let idx = currentPartWords.findIndex(_w => getWordId(_w) === w.id)
        if (idx === -1) {
          idx = 0
        }

        let currentText = currentPartWords[idx].textContent
        if (!currentText) {
          currentText = ''
        }

        currentPartWords[idx].textContent = currentText.concat(` ${text}`)
        setCursor(
          currentPartWords[idx],
          currentPartWords[idx].innerText.trim().length
        )
      })
    })

    setCurrentPartEdited()
    return true
  }

  /**
   * Paste text from clipboard
   * @param e - clipboard event
   */
  function pasteFromClipboard(e: ClipboardEvent) {
    e.preventDefault()
    if (
      !cursor.value ||
      !cursor.value.focusNode ||
      !cursor.value.focusNode.parentElement ||
      !keydownEvent.value ||
      !e.clipboardData ||
      !focusedWord.value ||
      !selectedPart.value
    ) {
      return
    }

    const text = e.clipboardData.getData('text/plain')

    if (cursor.value.type === 'Range') {
      insertInRange(text)
      return
    }

    if (cursor.value.type !== 'Caret') {
      return
    }

    const SAFARIisCursorAtTheBeginning = isSpaceElement(
      cursor.value.focusNode.parentElement
    )
    const OTHERSisCursorAtTheBeginning = cursor.value.focusOffset === 0

    if (SAFARIisCursorAtTheBeginning || OTHERSisCursorAtTheBeginning) {
      insertCharacterAtTheBeginningOfWord(`${text}`)
    } else {
      insertCharacterAtSpecificPosition(`${text}`, cursor.value.focusOffset)
    }
  }

  /**
   * Inserting a letter into the focused word
   * @param e - keyboard event
   */
  function handleTextInsert(e: KeyboardEvent) {
    if (
      !cursor.value ||
      !cursor.value.focusNode ||
      !cursor.value.focusNode.parentElement
    ) {
      return
    }

    const key = e.key.toLowerCase()

    if (key.length !== 1) {
      return
    }

    const isLetter = key >= 'a' && key <= 'z'
    const isNumber = key >= '0' && key <= '9'
    const isAllowedSymbol = allowedSymbols.includes(key)

    if (isLetter || isNumber || isAllowedSymbol) {
      const SAFARIisCursorAtTheBeginning = cursor.value.focusOffset === 0
      const OTHERSisCursorAtTheBeginning = isSpaceElement(
        cursor.value.focusNode.parentElement
      )

      if (cursor.value.type === 'Range') {
        insertInRange(e.key)
        return
      }

      if (SAFARIisCursorAtTheBeginning || OTHERSisCursorAtTheBeginning) {
        insertCharacterAtTheBeginningOfWord(e.key)
      }

      setCurrentPartEdited()
      // save()
      if (
        focusedWord.value &&
        !focusedWord.value.className.includes('single')
      ) {
        focusedWord.value.classList.add('single')
      }
    }
  }

  /**
   * Resolving different keys behavior
   */
  function handleEditorKeydown(e: KeyboardEvent, part: Word) {
    const canContinue = updateCursor()
    keydownEvent.value = e
    selectedPart.value = part

    if (!canContinue) {
      e.preventDefault()
      return
    }

    switch (e.key) {
      case 'Enter': {
        handlePressEnter()
        break
      }
      case 'Backspace': {
        handlePressBackspace()
        break
      }
      case 'Delete': {
        handlePressDelete()
        break
      }
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft': {
        setTimeout(updateCursor)
        break
      }
      default: {
        if (e.ctrlKey || e.metaKey) {
          break
          // TODO: check if 'z' key and revert last change
        }

        handleTextInsert(e)
      }
    }
  }

  /**
   * Resolving some input behavior cases
   */
  function handleEditorInput(e: InputEvent, part: Word) {
    const canContinue = updateCursor()
    if (selectedPart.value?.index !== part.index) {
      selectedPart.value = part
    }

    if (!canContinue) {
      return
    }

    // clearHistory()
    setCurrentPartEdited()
  }

  /**
   * Finds DOM words of a particular part
   */
  function getPartWords(index: number): HTMLElement[] {
    if (!conversationRef.value) {
      return []
    }

    const part = conversationRef.value.children[index]
    if (!part) {
      return []
    }
    const sentence = part.querySelector('.sentence')

    if (!sentence) {
      return []
    }

    return (
      (Array.from(sentence.children).filter(e =>
        e.className.includes('word')
      ) as HTMLElement[]) || []
    )
  }

  /**
   * Format each part and save
   * @param copy - defines whether it should make a copy of transcript
   */
  function formatAll(copy = false) {
    if (!conversationRef.value) {
      return null
    }
    const partsCount = conversationRef.value.children.length

    const domParts: HTMLElement[][] = []

    for (let i = 0; i < partsCount; i++) {
      domParts.push(getPartWords(i))
    }

    return transcriptEditor.formatAll(domParts, copy)
  }

  function watchCursor() {
    formatCursor()
  }

  /**
   * Reverts last change
   */
  function undo() {
    transcriptEditor.undo(undoHistory.value[undoHistory.value.length - 2])
  }

  onMounted(updateCursor)
  watch(cursor, watchCursor)
  watch(cursorHistory, function clearOldHistory() {
    if (cursorHistory.value.length > 10) {
      cursorHistory.value.shift()
    }
  })
  watch(undoHistory, function clearOldHistory() {
    if (undoHistory.value.length > 10) {
      undoHistory.value.shift()
    }
  })

  return {
    updTranscript,
    pasteFromClipboard,
    handleEditorKeydown,
    handleEditorInput,
    setMouseCursor,
    undo,
    formatAll,
    conversationRef,
    transcript: tr,
    wordRemoved,
    removedPart,
    touched,
    saveTranscript,
    focusedWord,
    cursor,
    version,
    currentPartIndex
  }
}
